import { showHttpError } from '@calendly/alert'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { flatMap } from 'lodash-es'

import {
  getOrganizationMemberships,
  getOrganizationInvitations,
  getUserGroups,
  getOrganizationMembershipsByIds,
  getOrganizationInvitationsByIds,
  getUserGroupsByIds,
  getMigrations,
  getMigrationsByIds
} from './api'
import { entitiesSelector } from './selectors'

const entityToSuggestion = (entity, type) => ({
  id: [type, entity.id].join('-'),
  type,
  entity
})

const membershipToSuggestion = (membership) => entityToSuggestion(membership, 'organization_membership')
const invitationToSuggestion = (invitation) => entityToSuggestion(invitation, 'organization_invitation')
const userGroupToSuggestion = (userGroup) => entityToSuggestion(userGroup, 'user_group')
const migrationsToSuggestion = (migration) => entityToSuggestion(migration, 'user_consolidation')

const ENTITIES = {
  memberships: {
    searchApiRequest: getOrganizationMemberships,
    getApiRequest: getOrganizationMembershipsByIds,
    dataMapper: membershipToSuggestion
  },
  invitations: {
    searchApiRequest: getOrganizationInvitations,
    getApiRequest: getOrganizationInvitationsByIds,
    dataMapper: invitationToSuggestion
  },
  userGroups: {
    searchApiRequest: getUserGroups,
    getApiRequest: getUserGroupsByIds,
    dataMapper: userGroupToSuggestion
  },
  migrations: {
    searchApiRequest: getMigrations,
    getApiRequest: getMigrationsByIds,
    dataMapper: migrationsToSuggestion
  }
}

export const fetchInitialValue = createAsyncThunk(
  'membershipsPicker/fetchInitialValue',
  async ({
    organizationMembershipIds = [],
    organizationInvitationIds = [],
    userGroupIds = [],
    organizationMigrationIds = []
  }, { dispatch, getState, rejectWithValue }) => {
    const ids = {
      memberships: organizationMembershipIds,
      invitations: organizationInvitationIds,
      userGroups: userGroupIds,
      migrations: organizationMigrationIds,
    }
    const entities = entitiesSelector(getState()).filter((entity) => !!ids[entity].length)
    const requests = entities.map((entity) => ENTITIES[entity].getApiRequest(ids[entity]))
    const responses = await Promise.all(requests)

    const [, error] = responses.find(([success]) => !success) || []
    if (error) {
      dispatch(showHttpError(error))
      return rejectWithValue([])
    }

    return flatMap(
      responses,
      ([, { results, collection }], index) => (results || collection).map(
        ENTITIES[entities[index]].dataMapper
      )
    )
  }
)

export const fetchSuggestions = createAsyncThunk(
  'membershipsPicker/fetchSuggestions',
  async (
    {
      query,
      filterForGroupAdmin = false,
      includeOnlyGroupMembers = false,
      userGroupId = null,
      userGroupIds = []
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    if (!query) {
      return []
    }

    const entities = entitiesSelector(getState())
    const requests = entities.map((entity) => ENTITIES[entity].searchApiRequest(
      query,
      userGroupId,
      filterForGroupAdmin,
      includeOnlyGroupMembers,
      userGroupIds
    ))
    const responses = await Promise.all(requests)

    const [, error] = responses.find(([success]) => !success) || []
    if (error) {
      dispatch(showHttpError(error))
      return rejectWithValue([])
    }

    return flatMap(
      responses,
      ([, { results, collection }], index) => (results || collection).map(
        ENTITIES[entities[index]].dataMapper
      )
    )
  }
)

const initialState = {
  entities: [],
  initializing: false,
  searching: false,
  suggestions: [],
  selectedSuggestions: [],
  orgAdminsToRemove: {
    organization_membership: {},
    organization_invitation: {},
    organization_migrations: {},
  }
}

const slice = createSlice({
  name: 'membershipsPicker',
  initialState,
  reducers: {
    setSelectedSuggestions(state, action) {
      state.selectedSuggestions = action.payload
    },
    addSelectedSuggestion(state, action) {
      state.selectedSuggestions.push(action.payload)
    },
    removeSelectedSuggestion: (state, { payload }) => {
      state.selectedSuggestions = state.selectedSuggestions.filter(({ id }) => id !== payload.id)
      if (payload.type === 'user_group') return
      const newOrgAdminsToRemove = { ...state.orgAdminsToRemove }
      delete newOrgAdminsToRemove[payload.type][payload.entity.id]
      state.orgAdminsToRemove = newOrgAdminsToRemove
    },
    updateOrgAdminToRemove(state, { payload: { type, entity, isOrgAdminToRemove } }) {
      state.orgAdminsToRemove = {
        ...state.orgAdminsToRemove,
        [type]: {
          ...state.orgAdminsToRemove[type],
          [entity.id]: isOrgAdminToRemove
        }
      }
    },
    setEntities(state, action) {
      state.entities = action.payload
    },
    resetState: () => initialState
  },
  extraReducers: (builder) => {
    builder.addCase(fetchInitialValue.pending, (state) => {
      state.initializing = true
    })
    builder.addCase(fetchInitialValue.fulfilled, (state, action) => {
      state.initializing = false
      state.selectedSuggestions = action.payload
    })
    builder.addCase(fetchInitialValue.rejected, (state) => {
      state.initializing = false
    })
    builder.addCase(fetchSuggestions.pending, (state) => {
      state.searching = true
    })
    builder.addCase(fetchSuggestions.fulfilled, (state, action) => {
      state.searching = false
      state.suggestions = action.payload
    })
    builder.addCase(fetchSuggestions.rejected, (state) => {
      state.searching = false
      state.suggestions = []
    })
  }
})

export const { reducer } = slice
export const {
  addSelectedSuggestion,
  removeSelectedSuggestion,
  updateOrgAdminToRemove,
  setEntities,
  resetState
} = slice.actions

const {
  setSelectedSuggestions
} = slice.actions

export const setInitialSelection = (initialValue) => (dispatch) => {
  const suggestions = flatMap(initialValue, (data, index) => data.map(ENTITIES[index].dataMapper))
  dispatch(setSelectedSuggestions(suggestions))
}
